/**
 * VisasApplications - Управление формами виз анкет
 * Реализует:
 * - Навигацию между анкетами
 * - Auto-save с debounce 1 секунду
 * - Обработку файлов (загрузка, отображение, удаление)
 * - Toggle для Yes/No вопросов
 * - Финальную отправку всех анкет
 */
export class VisasApplications {
	constructor(ajax) {
		this.ajax = ajax;
		this.container = document.querySelector('.visas-applications');
		if (!this.container) return;

		this.blockId = this.getBlockIdFromUrl();
		this.forms = [];
		this.currentFormIndex = 0;
		this.autoSaveTimeout = null;
		this.autoSaveDelay = 1000; // 1 секунда

		this.init();
	}

	init() {
		// Собираем все формы
		this.forms = Array.from(this.container.querySelectorAll('.visa-applications-form'));

		// Показываем первую форму
		this.showForm(0);

		// Биндим события для Yes/No вопросов
		this.bindQuestionToggles();

		// Проверяем готовность финальной отправки
		this.checkAllFormsComplete();
	}

	getBlockIdFromUrl() {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get('block_id');
	}

	/**
	 * Показать форму по индексу
	 */
	showForm(index) {
		if (index < 0 || index >= this.forms.length) return;

		// Скрываем все формы
		this.forms.forEach(form => form.style.display = 'none');

		// Показываем нужную форму
		this.forms[index].style.display = 'block';
		this.currentFormIndex = index;

		// Обновляем активный таб в навигации
		this.updateNavTabs();

		// Обновляем состояние кнопок навигации
		this.updateNavButtons();
	}

	/**
	 * Обновить активный таб в навигации
	 */
	updateNavTabs() {
		const navItems = this.container.querySelectorAll('.visas-applications-nav__item-link');
		navItems.forEach((link, index) => {
			if (index === this.currentFormIndex) {
				link.classList.add('active');
			} else {
				link.classList.remove('active');
			}
		});
	}

	/**
	 * Обновить кнопки навигации
	 */
	updateNavButtons() {
		const currentForm = this.forms[this.currentFormIndex];
		const prevBtn = currentForm.querySelector('.visa-applications-form__nav-prev');
		const nextBtn = currentForm.querySelector('.visa-applications-form__nav-next');

		// Отключаем "Предыдущая" на первой форме
		if (prevBtn) {
			prevBtn.disabled = this.currentFormIndex === 0;
		}

		// Отключаем "Следующая" на последней форме
		if (nextBtn) {
			nextBtn.disabled = this.currentFormIndex === this.forms.length - 1;
		}
	}

	/**
	 * Показать предыдущую форму
	 */
	showPreviousForm() {
		if (this.currentFormIndex > 0) {
			this.showForm(this.currentFormIndex - 1);
		}
	}

	/**
	 * Показать следующую форму
	 */
	showNextForm() {
		if (this.currentFormIndex < this.forms.length - 1) {
			this.showForm(this.currentFormIndex + 1);
		}
	}

	/**
	 * Привязать события для Yes/No вопросов
	 */
	bindQuestionToggles() {
		this.forms.forEach(form => {
			const questions = form.querySelectorAll('.visa-applications-form__question');

			questions.forEach(question => {
				const yesInput = question.querySelector('.visa-applications-form__question-input--yes');
				const noInput = question.querySelector('.visa-applications-form__question-input--no');
				const moreSection = question.querySelector('.visa-applications-form__question-more');

				if (!yesInput || !noInput || !moreSection) return;

				const textarea = moreSection.querySelector('textarea');

				// Обработчик для "Да"
				yesInput.addEventListener('change', () => {
					if (yesInput.checked) {
						$(moreSection).slideDown(300);
						if (textarea) {
							textarea.disabled = false;
							textarea.required = true;
						}
					}
				});

				// Обработчик для "Нет"
				noInput.addEventListener('change', () => {
					if (noInput.checked) {
						$(moreSection).slideUp(300);
						if (textarea) {
							textarea.disabled = true;
							textarea.required = false;
							textarea.value = '';
						}
					}
				});
			});
		});
	}

	/**
	 * Запланировать auto-save с debounce
	 */
	scheduleAutoSave(form) {
		// Очищаем предыдущий таймер
		if (this.autoSaveTimeout) {
			clearTimeout(this.autoSaveTimeout);
		}

		// Показываем индикатор сохранения
		const savingIndicator = form.querySelector('.visa-applications-form__saving');
		if (savingIndicator) {
			savingIndicator.style.opacity = '0.5';
		}

		// Устанавливаем новый таймер
		this.autoSaveTimeout = setTimeout(() => {
			this.saveForm(form);
		}, this.autoSaveDelay);
	}

	/**
	 * Сохранить форму через AJAX
	 */
	async saveForm(form) {
		const formData = new FormData(form);
		const formId = form.dataset.formId;

		// Добавляем дополнительные параметры
		formData.append('action', 'saveForm');
		formData.append('form_id', formId);

		try {
			const response = await this.ajax.post('/ajax.php?mid=17', formData);

			// Скрываем индикатор сохранения
			const savingIndicator = form.querySelector('.visa-applications-form__saving');
			if (savingIndicator) {
				savingIndicator.style.opacity = '1';
			}

			// Обновляем статус анкеты в навигации
			this.updateFormStatus(formId, response.status);

			// Проверяем готовность финальной отправки
			this.checkAllFormsComplete();

			console.log('Form saved:', response);
		} catch (error) {
			console.error('Error saving form:', error);
			alert('Ошибка при сохранении формы. Попробуйте еще раз.');
		}
	}

	/**
	 * Обновить статус анкеты в навигации
	 */
	updateFormStatus(formId, status) {
		const navItem = this.container.querySelector(`.visas-applications-nav__item[data-form-id="${formId}"]`);
		if (!navItem) return;

		const statusEl = navItem.querySelector('.visas-applications-nav__item-status');
		const icon = statusEl.querySelector('.material-symbols-sharp');
		const text = statusEl.querySelector('span:last-child');

		// Удаляем старые классы
		statusEl.classList.remove('visas-applications-nav__item-status--done', 'visas-applications-nav__item-status--process');

		if (status == 1) {
			// Готово
			statusEl.classList.add('visas-applications-nav__item-status--done');
			icon.textContent = 'check_circle';
			text.textContent = 'Заполнена';
		} else if (status == 0) {
			// В процессе
			statusEl.classList.add('visas-applications-nav__item-status--process');
			icon.textContent = 'blur_circular';
			text.textContent = 'В процессе';
		} else {
			// Пустая
			icon.textContent = 'circle';
			text.textContent = 'Пустая анкета';
		}
	}

	/**
	 * Проверить готовность всех анкет
	 */
	async checkAllFormsComplete() {
		try {
			const formData = new FormData();
			formData.append('action', 'checkFormsStatus');
			formData.append('block_id', this.blockId);

			const response = await this.ajax.post('/ajax.php?mid=17', formData);

			const allComplete = response.all_complete;
			const bottomSection = this.container.querySelector('.visa-applications-form__bottom');

			if (bottomSection) {
				// Показываем секцию всегда
				bottomSection.style.display = 'flex';

				// Включаем/отключаем чекбоксы и email в зависимости от готовности
				const checkboxes = bottomSection.querySelectorAll('input[type="checkbox"]');
				const emailInput = bottomSection.querySelector('input[type="email"]');
				const submitBtn = bottomSection.querySelector('.visa-applications-form__submit');

				checkboxes.forEach(checkbox => {
					checkbox.disabled = !allComplete;
				});

				if (emailInput) {
					emailInput.disabled = !allComplete;
				}

				if (submitBtn) {
					submitBtn.disabled = !allComplete;
				}

				// Если все готово, проверяем готовность к отправке
				if (allComplete) {
					this.checkSubmitReady();
				}
			}
		} catch (error) {
			console.error('Error checking forms status:', error);
		}
	}

	/**
	 * Проверить готовность к финальной отправке
	 */
	checkSubmitReady() {
		const agree1 = document.getElementById('chk_agree_1');
		const agree2 = document.getElementById('chk_agree_2');
		const agree3 = document.getElementById('chk_agree_3');
		const emailInput = document.getElementById('str_email_agent');
		const submitBtn = this.container.querySelector('.visa-applications-form__submit');

		if (!agree1 || !agree2 || !agree3 || !emailInput || !submitBtn) return;

		const allAgreed = agree1.checked && agree2.checked && agree3.checked;
		const emailFilled = emailInput.value.trim() !== '';

		submitBtn.disabled = !(allAgreed && emailFilled);
	}

	/**
	 * Обработка выбора файла
	 */
	handleFileSelect(input) {
		const file = input.files[0];
		if (!file) return;

		const formFile = input.closest('.form-file');
		const resultsLink = formFile.querySelector('.form-file__results-item-link');
		const resultsName = formFile.querySelector('.form-file__results-item-name');
		const mainBlock = formFile.querySelector('.form-file__main');
		const resultsBlock = formFile.querySelector('.form-file__results');

		// Показываем results, скрываем main
		if (resultsBlock) resultsBlock.classList.add('active');
		if (mainBlock) mainBlock.style.display = 'none';

		// Создаем временный URL для preview
		const fileUrl = URL.createObjectURL(file);
		resultsLink.href = fileUrl;
		resultsName.textContent = file.name;

		// Триггерим auto-save для загрузки файла
		const form = input.closest('form');
		if (form) {
			this.scheduleAutoSave(form);
		}
	}

	/**
	 * Удалить файл
	 */
	async clearFile(btn, fieldName) {
		const form = btn.closest('form');
		const formId = form.dataset.formId;
		const formFile = btn.closest('.form-file');
		const input = form.querySelector(`input[name="${fieldName}"]`);
		const mainBlock = formFile.querySelector('.form-file__main');
		const resultsBlock = formFile.querySelector('.form-file__results');

		try {
			const formData = new FormData();
			formData.append('action', 'deleteFile');
			formData.append('form_id', formId);
			formData.append('field_name', fieldName);

			const response = await this.ajax.post('/ajax.php?mid=17', formData);

			// Скрываем results, показываем main
			if (resultsBlock) resultsBlock.classList.remove('active');
			if (mainBlock) mainBlock.style.display = 'flex';

			// Очищаем input
			if (input) {
				input.value = '';
			}

			// Триггерим auto-save
			this.scheduleAutoSave(form);

			console.log('File deleted:', response);
		} catch (error) {
			console.error('Error deleting file:', error);
			alert('Ошибка при удалении файла. Попробуйте еще раз.');
		}
	}

	/**
	 * Финальная отправка всех анкет
	 */
	async submitAllForms(finalForm) {
		const submitBtn = finalForm.querySelector('.visa-applications-form__submit');

		// Отключаем кнопку
		if (submitBtn) {
			submitBtn.disabled = true;
			submitBtn.textContent = 'Отправка...';
		}

		try {
			const formData = new FormData();
			formData.append('action', 'submitAllForms');
			formData.append('block_id', this.blockId);
			formData.append('email_agent', finalForm.querySelector('input[name="str_email_agent"]').value);

			const response = await this.ajax.post('/ajax.php?mid=17', formData);

			if (response.success) {
				alert('Анкеты успешно отправлены! Проверьте почту для подтверждения.');
				// Можно редиректить на страницу успеха
				// window.location.href = '/visa/success/';
			} else {
				throw new Error(response.error || 'Ошибка при отправке');
			}
		} catch (error) {
			console.error('Error submitting forms:', error);
			alert('Ошибка при отправке анкет. Попробуйте еще раз.');

			// Включаем кнопку обратно
			if (submitBtn) {
				submitBtn.disabled = false;
				submitBtn.textContent = 'Отправить анкеты';
			}
		}
	}
}